import { useEffect, useRef } from "react";
import { API } from "./api";

/**
 * Connect to the realtime tx WebSocket. Auth happens via the httpOnly
 * `session_token` cookie sent by the browser during the WS handshake — no
 * token-in-URL needed for production.
 *
 * For testing where a cookie is unavailable, pass an explicit `token`.
 */
export function useRealtimeTx({ onTransaction, enabled = true, token = null }) {
    const wsRef = useRef(null);
    const reconnectRef = useRef(null);
    const attemptsRef = useRef(0);
    const cbRef = useRef(onTransaction);

    useEffect(() => {
        cbRef.current = onTransaction;
    }, [onTransaction]);

    useEffect(() => {
        if (!enabled) return undefined;

        let stopped = false;

        const buildUrl = () => {
            const base = API.replace(/^http/, "ws");
            return token
                ? `${base}/ws/transactions?token=${encodeURIComponent(token)}`
                : `${base}/ws/transactions`;
        };

        const connect = () => {
            if (stopped) return;
            const ws = new WebSocket(buildUrl());
            wsRef.current = ws;

            ws.onopen = () => {
                attemptsRef.current = 0;
            };

            ws.onmessage = (e) => {
                try {
                    const data = JSON.parse(e.data);
                    if (data?.event === "new_transaction" && data.transaction) {
                        cbRef.current?.(data.transaction, data.source);
                    }
                } catch (err) {
                    // Non-JSON frame (e.g. server-side ping) — log at debug
                    // level so we don't pollute production console.
                    console.debug("[realtime-tx] non-JSON frame skipped:", err);
                }
            };

            ws.onclose = () => {
                if (stopped) return;
                attemptsRef.current += 1;
                const delay = Math.min(1000 * 2 ** attemptsRef.current, 15000);
                reconnectRef.current = setTimeout(connect, delay);
            };

            ws.onerror = () => {
                try { ws.close(); } catch (err) {
                    console.debug("[realtime-tx] ws.close on error failed:", err);
                }
            };
        };

        connect();

        return () => {
            stopped = true;
            if (reconnectRef.current) {
                clearTimeout(reconnectRef.current);
                reconnectRef.current = null;
            }
            if (wsRef.current) {
                try { wsRef.current.close(); } catch (err) {
                    console.debug("[realtime-tx] ws.close on cleanup failed:", err);
                }
                wsRef.current = null;
            }
        };
    }, [enabled, token]);
}
